'use client';

/**
 * VendorOpportunityPage — CQCC Phase 6 / Surface 4
 *
 * Shows D4 vendor_enrichment_opportunity rows for the selected site, sorted by
 * opportunity_score. Lets CS trigger an enrichment wave per category.
 *
 * Route: /data-quality/vendor-opportunity
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, RefreshCw, Package, TrendingUp, ArrowUpRight } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useSite } from '@/contexts/SiteContext';
import { catalogColors } from '@/components/catalog/catalogColors';

// ─── Types ────────────────────────────────────────────────────────────────────

interface OpportunityRow {
  id: string;
  site_id: string;
  site_name: string;
  site_domain: string;
  category_path: string;
  computed_at: string;
  products_in_category: number;
  products_vendor_enriched: number;
  products_vendor_enrichable: number;
  projected_coverage_lift: number | null;
  current_comparison_health_pct: number | null;
  projected_comparison_health_pct: number | null;
  opportunity_score: number | null;
}

type LoadState = 'idle' | 'loading' | 'loaded' | 'error';
type ActionState = { loading: boolean; result: string | null; error: string | null };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function confidenceLabel(row: OpportunityRow): { label: string; variant: 'default' | 'secondary' | 'outline' } {
  if (row.products_vendor_enrichable >= 10) return { label: 'High',   variant: 'default'   };
  if (row.products_vendor_enrichable >= 4)  return { label: 'Medium', variant: 'secondary' };
  return { label: 'Low', variant: 'outline' };
}

function costEstimate(enrichable: number): string {
  // ~$0.50 per product enriched (rough estimate; actual cost depends on vendor source depth)
  const est = enrichable * 0.5;
  return est < 1 ? `<$1` : `~$${Math.round(est)}`;
}

function pct(val: number | null): string {
  if (val == null) return '—';
  return `${(val * 100).toFixed(0)}%`;
}

function liftColor(lift: number | null): string {
  // lift is stored as 0-1 fraction (e.g. 0.12 = 12pp)
  if (lift == null || lift < 0.02)  return catalogColors.textMuted ?? '#7E8294';
  if (lift < 0.08)                  return catalogColors.accentTeal ?? '#4CC9F0';
  return catalogColors.accentGreen ?? '#4ADE80';
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function VendorOpportunityPage() {
  const { selectedSite } = useSite();
  const siteId = selectedSite?.id ?? null;

  // Tracks the site that was current when a POST action was dispatched.
  // Checked before applying POST response state to guard against site-switch races.
  const activeSiteRef = React.useRef<string | null>(siteId);
  activeSiteRef.current = siteId;

  const [rows, setRows] = useState<OpportunityRow[]>([]);
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [migrationPending, setMigrationPending] = useState(false);
  const [actionState, setActionState] = useState<ActionState>({ loading: false, result: null, error: null });

  // Reset action feedback when the user switches sites so the button re-enables
  // and stale success/error messages from the previous site are cleared.
  useEffect(() => {
    setActionState({ loading: false, result: null, error: null });
  }, [siteId]);

  useEffect(() => {
    if (!siteId) return;
    const controller = new AbortController();
    setLoadState('loading');
    setLoadError(null);

    authFetch(`/api/admin/cs-quality/vendor-opportunity/${siteId}`, { signal: controller.signal })
      .then(res => res.json().then(data => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (controller.signal.aborted) return;
        if (!ok) throw new Error(data.message ?? data.error ?? `HTTP error`);
        setMigrationPending(data.migrationPending ?? false);
        setRows(data.rows ?? []);
        setLoadState('loaded');
      })
      .catch((e: unknown) => {
        if (controller.signal.aborted) return;
        setLoadError(e instanceof Error ? e.message : String(e));
        setLoadState('error');
      });

    return () => controller.abort();
  }, [siteId]);

  const handleScoreRefresh = useCallback(async () => {
    if (!siteId) return;
    const dispatchedForSite = siteId;
    setActionState({ loading: true, result: null, error: null });
    try {
      const res  = await authFetch(`/api/admin/cs-quality/customer/${siteId}/vendor-offer`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message ?? data.error ?? `HTTP ${res.status}`);
      // Discard if the user switched sites while the POST was in flight.
      if (activeSiteRef.current !== dispatchedForSite) return;
      setActionState({ loading: false, result: data.message ?? 'Scores refreshed', error: null });
      setRows(data.rows ?? []);
      setLoadState('loaded');
    } catch (e: unknown) {
      if (activeSiteRef.current !== dispatchedForSite) return;
      setActionState({ loading: false, result: null, error: e instanceof Error ? e.message : String(e) });
    }
  }, [siteId]);

  // ─── Render ───────────────────────────────────────────────────────────────

  const noSite = !siteId;

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Package size={20} style={{ color: catalogColors.accentTeal }} />
            Vendor Enrichment Opportunity
          </h1>
          <p className="text-sm mt-1" style={{ color: catalogColors.textMuted }}>
            D4 scanner — projected comparison-health lift per category from vendor enrichment.
            Scores are estimated; labeled "limited data" until live enrichment measurements exist.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={handleScoreRefresh}
          disabled={noSite || actionState.loading}
        >
          {actionState.loading
            ? <RefreshCw size={14} className="animate-spin mr-1" />
            : <RefreshCw size={14} className="mr-1" />
          }
          Refresh scores
        </Button>
      </div>

      {/* Action feedback */}
      {actionState.result && (
        <Alert>
          <AlertDescription className="text-sm text-green-400">{actionState.result}</AlertDescription>
        </Alert>
      )}
      {actionState.error && (
        <Alert variant="destructive">
          <AlertCircle size={14} className="mr-1" />
          <AlertDescription className="text-sm">{actionState.error}</AlertDescription>
        </Alert>
      )}

      {noSite && (
        <Alert>
          <AlertDescription className="text-sm">Select a site to view vendor enrichment opportunities.</AlertDescription>
        </Alert>
      )}

      {migrationPending && (
        <Alert>
          <AlertDescription className="text-sm">
            D4 migration pending — vendor enrichment opportunity data not yet available.
          </AlertDescription>
        </Alert>
      )}

      {/* Main card */}
      {!noSite && !migrationPending && (
        <Card>
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <TrendingUp size={14} style={{ color: catalogColors.accentTeal }} />
              Enrichment opportunities
              {loadState === 'loaded' && rows.length > 0 && (
                <span className="ml-1 text-xs font-normal" style={{ color: catalogColors.textMuted }}>
                  — {rows.length} categor{rows.length === 1 ? 'y' : 'ies'} with vendor data available
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {loadState === 'loading' && (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            )}

            {loadState === 'error' && (
              <Alert variant="destructive">
                <AlertCircle size={14} className="mr-1" />
                <AlertDescription className="text-sm">{loadError}</AlertDescription>
              </Alert>
            )}

            {loadState === 'loaded' && rows.length === 0 && (
              <p className="text-sm py-4 text-center" style={{ color: catalogColors.textMuted }}>
                No vendor enrichment opportunities found for this site.
                This means either no vendor sources are registered (vendor_site_sources) or
                no products match available vendor brands.
              </p>
            )}

            {loadState === 'loaded' && rows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 pr-3 font-medium" style={{ color: catalogColors.textMuted }}>Category</th>
                      <th className="text-right py-2 px-2 font-medium" style={{ color: catalogColors.textMuted }}>Products</th>
                      <th className="text-right py-2 px-2 font-medium" style={{ color: catalogColors.textMuted }}>Enrichable</th>
                      <th className="text-right py-2 px-2 font-medium" style={{ color: catalogColors.textMuted }}>Current health</th>
                      <th className="text-right py-2 px-2 font-medium" style={{ color: catalogColors.textMuted }}>After enrichment</th>
                      <th className="text-right py-2 px-2 font-medium" style={{ color: catalogColors.textMuted }}>Lift</th>
                      <th className="text-right py-2 px-2 font-medium" style={{ color: catalogColors.textMuted }}>Est. cost</th>
                      <th className="text-center py-2 px-2 font-medium" style={{ color: catalogColors.textMuted }}>Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const conf = confidenceLabel(row);
                      return (
                        <tr key={row.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                          <td className="py-2 pr-3 font-medium max-w-[220px] truncate" title={row.category_path}>
                            {row.category_path}
                          </td>
                          <td className="text-right py-2 px-2" style={{ color: catalogColors.textMuted }}>
                            {row.products_in_category.toLocaleString()}
                          </td>
                          <td className="text-right py-2 px-2">
                            <span style={{ color: catalogColors.accentTeal }}>
                              {row.products_vendor_enrichable.toLocaleString()}
                            </span>
                          </td>
                          <td className="text-right py-2 px-2" style={{ color: catalogColors.textMuted }}>
                            {pct(row.current_comparison_health_pct)}
                          </td>
                          <td className="text-right py-2 px-2">
                            <span style={{ color: catalogColors.accentGreen }}>
                              {pct(row.projected_comparison_health_pct)}
                            </span>
                          </td>
                          <td className="text-right py-2 px-2">
                            <span style={{ color: liftColor(row.projected_coverage_lift), fontWeight: 600 }}>
                              {row.projected_coverage_lift != null
                                ? `+${(row.projected_coverage_lift * 100).toFixed(1)}pp`
                                : '—'}
                            </span>
                          </td>
                          <td className="text-right py-2 px-2" style={{ color: catalogColors.textMuted }}>
                            {costEstimate(row.products_vendor_enrichable)}
                          </td>
                          <td className="text-center py-2 px-2">
                            <Badge variant={conf.variant} className="text-[10px] px-1.5 py-0">
                              {conf.label}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <p className="text-[10px] mt-3" style={{ color: catalogColors.textMuted }}>
                  Scores are estimates (limited data). Lift = enrichable-ratio × 20pp cap, capped at remaining gap to 100%. Health values are 0–1 fractions (×100 = %).
                  <ArrowUpRight size={10} className="inline ml-0.5" />
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
